import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { client } from "@/api";

// Every figure is measured or null (never a guess). Mirrors the StorageMetrics shape in
// backend/src/routes/admin/storage/index.ts.
export interface StorageMetrics {
  totalSize: number | null;
  totalFiles: number | null;
  // Where the totals came from: Cloudflare Analytics, the cached complete listing, or nowhere.
  source: "analytics" | "breakdown" | null;
  measuredAt: string | null;
  // True when the totals are from a listing that stopped at its page cap (lower bounds).
  partial: boolean;
  // Null when the Analytics API is not configured or failed.
  recentUploads: {
    last24h: number;
    last7d: number;
    last30d: number;
  } | null;
}

// Mirrors backend/src/lib/r2-key-shapes.ts STORAGE_CATEGORIES.
export type StorageCategory =
  | "original"
  | "transcoded"
  | "cover"
  | "fragmented"
  | "rendition"
  | "download"
  | "exchange_preview"
  | "diagram"
  | "trash"
  | "other";

export interface StorageTally {
  bytes: number;
  count: number;
}

export type StorageCategoryTotals = Record<StorageCategory, StorageTally>;

export interface StorageBreakdownGroup {
  categories: StorageCategoryTotals;
  total_bytes: number;
  total_count: number;
}

// The cached full-bucket listing (#1986). Mirrors backend/src/lib/storage-breakdown.ts.
export interface StorageBreakdown {
  computed_at: string;
  list_calls: number;
  objects: number;
  partial: boolean;
  partial_reason: string | null;
  total_bytes: number;
  categories: StorageCategoryTotals;
  teams: (StorageBreakdownGroup & { public_id: string; name: string })[];
  individuals: StorageBreakdownGroup;
  unattributed: StorageBreakdownGroup & {
    reasons: { no_game_in_key: StorageTally; game_not_in_d1: StorageTally };
  };
}

export type StorageBreakdownResponse =
  | { computed: false }
  | { computed: true; breakdown: StorageBreakdown };

export type TeamStorageDetails =
  | { computed: false; teamId: string }
  | {
      computed: true;
      teamId: string;
      teamName: string;
      computedAt: string;
      partial: boolean;
      categories: StorageCategoryTotals | null;
      totalSize: number;
      fileCount: number;
    };

export function useStorageOverview() {
  return useQuery({
    queryKey: ["storage", "overview"],
    queryFn: async () => {
      const response = await (client).admin.storage.overview.$get();
      
      if (!response.ok) {
        throw new Error("Failed to fetch storage overview");
      }
      
      const data = await response.json();
      return data as StorageMetrics;
    },
    staleTime: 4 * 60 * 60 * 1000, // Consider data stale after 4 hours
    gcTime: 5 * 60 * 60 * 1000, // Keep in cache for 5 hours
    refetchInterval: false, // Don't automatically refetch
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
}

// The cached breakdown. Reads one KV key on the backend; never lists the bucket, and nothing
// refetches it on a timer or on focus — only `useRefreshStorageBreakdown` relists.
export function useStorageBreakdown() {
  return useQuery({
    queryKey: ["storage", "breakdown"],
    queryFn: async () => {
      const response = await client.admin.storage.breakdown.$get();
      if (!response.ok) {
        throw new Error("Failed to fetch storage breakdown");
      }
      return (await response.json()) as StorageBreakdownResponse;
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

// Relist the whole bucket and replace the cached breakdown. Read-only on R2.
export function useRefreshStorageBreakdown() {
  const queryClient = useQueryClient();
  return useMutation<StorageBreakdownResponse, Error, void>({
    mutationFn: async () => {
      const response = await client.admin.storage.breakdown.refresh.$post();
      if (!response.ok) {
        let message = `Refresh failed (${response.status})`;
        try {
          const body = (await response.json()) as { message?: string };
          if (body?.message) message = body.message;
        } catch {
          // non-JSON body — keep the generic message
        }
        throw new Error(message);
      }
      return (await response.json()) as StorageBreakdownResponse;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["storage", "breakdown"], data);
      // The bucket total card falls back to this listing, so it changes with every refresh. Seed it
      // from the response rather than refetching: the overview rereads the listing from KV, which
      // is eventually consistent and can still return the previous value (or none) right after the
      // write. An Analytics figure, when there is one, still takes precedence.
      if (data.computed) {
        const b = data.breakdown;
        queryClient.setQueryData<StorageMetrics>(["storage", "overview"], (prev) =>
          !prev || prev.source === "analytics"
            ? prev
            : {
                ...prev,
                totalSize: b.total_bytes,
                totalFiles: b.objects,
                source: "breakdown",
                measuredAt: b.computed_at,
                partial: b.partial,
              }
        );
      }
      queryClient.invalidateQueries({ queryKey: ["storage", "team"] });
    },
  });
}

export function useTeamStorage(teamId: string) {
  return useQuery({
    queryKey: ["storage", "team", teamId],
    queryFn: async () => {
      const response = await (client).admin.storage.team[":teamId"].$get({
        param: { teamId },
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch team storage details");
      }
      
      const data = await response.json();
      return data as TeamStorageDetails;
    },
    enabled: !!teamId,
  });
}

export interface StorageRecomputeStats {
  owners_scanned: number;
  owners_changed: number;
  bytes_before: number;
  bytes_after: number;
  rows_read: number;
  duration_ms: number;
}

// Recompute the storage counters now (#631) — the daily cron's job, on demand. Pass a team or
// user public id to recompute just that owner; omit both to recompute everyone.
export function useRecomputeStorage() {
  const queryClient = useQueryClient();
  return useMutation<StorageRecomputeStats, Error, { teamId?: string; userId?: string }>({
    mutationFn: async ({ teamId, userId }) => {
      const response = await client.admin.storage["recompute-counters"].$post({
        json: {
          confirm: true as const,
          ...(teamId ? { team_id: teamId } : {}),
          ...(userId ? { user_id: userId } : {}),
        },
      });
      if (!response.ok) {
        let message = `Recompute failed (${response.status})`;
        try {
          const body = (await response.json()) as { message?: string };
          if (body?.message) message = body.message;
        } catch {
          // non-JSON body — keep the generic message
        }
        throw new Error(message);
      }
      return (await response.json()) as StorageRecomputeStats;
    },
    onSuccess: (_data, variables) => {
      // The counters feed the team and user detail pages and the usage numbers.
      if (variables.teamId) queryClient.invalidateQueries({ queryKey: ["team", variables.teamId] });
      else queryClient.invalidateQueries();
    },
  });
}
